import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { dismissAnnouncement, readDismissedAnnouncementIds } from "@/lib/siteConfigStorage";

const SiteConfigContext = createContext(null);

const POLL_MS = 60000;

export function SiteConfigProvider({ children }) {
    const [config, setConfig] = useState({ beta_mode_enabled: false, announcements: [] });
    const [loading, setLoading] = useState(true);
    const [dismissedIds, setDismissedIds] = useState(() => readDismissedAnnouncementIds());

    const load = useCallback(async (audience) => {
        try {
            const params = audience ? { audience } : undefined;
            const { data } = await api.get("/site-config", { params });
            setConfig({
                beta_mode_enabled: Boolean(data.beta_mode_enabled),
                announcements: data.announcements || [],
                updated_at: data.updated_at,
            });
        } catch (_e) {
            setConfig((prev) => ({ ...prev, announcements: prev.announcements || [] }));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            if (!mounted) return;
            await load();
        };
        run();
        const timer = setInterval(run, POLL_MS);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
    }, [load]);

    const dismiss = useCallback((id) => {
        dismissAnnouncement(id);
        setDismissedIds(readDismissedAnnouncementIds());
    }, []);

    const visibleAnnouncements = useMemo(
        () => (config.announcements || []).filter((item) => !dismissedIds.has(item.id)),
        [config.announcements, dismissedIds],
    );

    const value = useMemo(
        () => ({
            loading,
            betaModeEnabled: Boolean(config.beta_mode_enabled),
            announcements: visibleAnnouncements,
            allAnnouncements: config.announcements || [],
            dismissAnnouncement: dismiss,
            refreshSiteConfig: load,
        }),
        [loading, config.beta_mode_enabled, config.announcements, visibleAnnouncements, dismiss, load],
    );

    return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export function useSiteConfig() {
    const ctx = useContext(SiteConfigContext);
    if (!ctx) {
        return {
            loading: false,
            betaModeEnabled: false,
            announcements: [],
            allAnnouncements: [],
            dismissAnnouncement: () => {},
            refreshSiteConfig: async () => {},
        };
    }
    return ctx;
}
